import { FC } from 'react'
import gql from 'graphql-tag'
import styled from 'styled-components'
import { CoverLetterListItemMobile_CoverLetterFragment } from 'generated/graphql'
import { COVER_LETTER_TYPE_TEXT_OBJECT } from '@app.feature/coverletter/constant'
import Link, { LinkProps } from 'components/Link'
import useKeywordHighlighter from 'hooks/useKeywordHighlighter'
import arrayFilterJoin from 'utils/arrayFilterJoin'
import { getPassedAtText, getSpecInfo } from 'utils/coverLetter'
import { useSearchStateContext } from '../../../useSearchState'
import CoverLetterListItemMobileContent from './CoverLetterListItemMobileContent'

interface CoverLetterListItemMoProps {
  coverLetterHighlight?: string[] | null
  coverLetter?: CoverLetterListItemMobile_CoverLetterFragment | null
  LinkProps: LinkProps
}

const CoverLetterListItemMo: FC<CoverLetterListItemMoProps> = ({
  coverLetter,
  LinkProps,
  coverLetterHighlight,
}) => {
  const { search } = useSearchStateContext()

  const highlightedOrgName = useKeywordHighlighter({
    text: coverLetter?.organizationName || '-',
    keyword: search.organizationName,
  })
  const highlightedRole = useKeywordHighlighter({
    text: coverLetter?.role || '-',
    keyword: search.role,
  })
  const passedAt = getPassedAtText(coverLetter)

  const typesText = coverLetter?.types
    ?.map((type) => {
      if (!type) {
        return <></>
      }
      return `${COVER_LETTER_TYPE_TEXT_OBJECT[type]}`
    })
    .join(', ')

  const highlightContent: string[] = []
  if (coverLetterHighlight && coverLetterHighlight.length > 0) {
    highlightContent.push(coverLetterHighlight[0])
  }

  return (
    <StyledWrapper>
      <Link className="link" {...LinkProps}>
        <div className="list-item-container">
          <p
            className="mobile-basic-info"
            dangerouslySetInnerHTML={{
              __html: arrayFilterJoin([
                highlightedOrgName,
                highlightedRole,
                passedAt,
                typesText,
              ]),
            }}
          />
          <h4 className="spec-info">{getSpecInfo(coverLetter)}</h4>
          <CoverLetterListItemMobileContent
            content={coverLetter?.contentSummaryMobile || ''}
            highlight={highlightContent}
            typographyProps={{
              className: 'coverletter-mobile-content content-ellipsis',
            }}
          />
        </div>
      </Link>
    </StyledWrapper>
  )
}

export default CoverLetterListItemMo

const StyledWrapper = styled.div`
  .coverletter-mobile-content {
    & b {
      background-color: #bbe6ff;
      color: #333333;
    }
  }

  .list-item-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .content-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 24px;
    max-height: 48px;

    & b {
      color: #333333;
    }
  }

  .mobile-basic-info {
    font-size: 16px;
    margin-bottom: 5px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    line-height: 24px;
    max-height: 24px;

    & .highlight {
      font-weight: 600;
    }
  }

  .spec-info {
    margin-bottom: 5px;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    line-height: 24px;
    max-height: 24px;
  }

  .link {
    &:hover {
      text-decoration: underline;
    }
  }
`

gql`
  fragment CoverLetterListItemMobile_coverLetter on CoverLetter {
    id
    role
    contentSummaryMobile: content(length: 150)
    organizationName
    types
    ...CoverLetterUtils_getSpecInfo_coverLetter
    ...CoverLetterUtils_getPassedAtText_coverLetter
  }
`
